import React from 'react';
import { Check, RefreshCw, Diamond } from 'lucide-react';
export function ConstitutionSection() {
  return <section className="py-24 px-6 md:px-12 lg:px-24 bg-white border-t border-gray-100" data-id="element-126">
      <div className="max-w-7xl mx-auto" data-id="element-127">
        <div className="mb-16" data-id="element-128">
          <span className="text-sm font-bold tracking-widest uppercase text-[#0066FF] mb-4 block font-sans" data-id="element-129">
            The Solution
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 font-['Syne'] leading-tight" data-id="element-130">
            Your agents need more than a prompt.
            <br data-id="element-131" />
            They need a constitution.
          </h2>
          <p className="text-xl text-gray-600 font-light max-w-3xl" data-id="element-132">
            A prompt tells an agent what to do right now. A constitution defines
            what an agent is allowed to do, must do, and must never do —
            persistently, across sessions, across tasks. Synthia builds it from
            the institutional intelligence you already own.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-133">
          {/* Left Column - Narrative */}
          <div className="flex flex-col gap-8" data-id="element-134">
            <h3 className="text-2xl font-bold font-['Syne']" data-id="element-135">
              Three layers, one integration.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed font-['Lora']" data-id="element-136">
              You don't start from scratch. Most of what your agents need,
              Synthia already produces through its conversational guardrail
              system. The agent constitution is assembled from three layers —
              and your existing customers are already halfway there.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed font-['Lora']" data-id="element-137">
              The compliance expert never has to look at code. The developer
              never has to understand pharmaceutical regulations. Synthia
              translates between them. One clean integration package. One line
              of code.
            </p>

            <div className="mt-8 border-l-4 border-[#0066FF] pl-6 py-2" data-id="element-138">
              <p className="text-xl italic text-gray-800 font-['Lora'] mb-4" data-id="element-139">
                "You can give an intern the keys to the law library. It doesn't
                make them a lawyer."
              </p>
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-140">
                — The Synthia principle
              </p>
            </div>
          </div>

          {/* Right Column - Layer Cards */}
          <div className="flex flex-col gap-6" data-id="element-141">
            {/* Layer 1 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:border-green-200 transition-colors" data-id="element-142">
              <div className="flex items-start gap-4" data-id="element-143">
                <div className="p-2 bg-green-100 rounded-full text-green-600 mt-1" data-id="element-144">
                  <Check className="w-4 h-4" data-id="element-145" />
                </div>
                <div data-id="element-146">
                  <span className="text-xs font-bold text-green-600 uppercase tracking-wider mb-2 block" data-id="element-147">
                    ALREADY BUILT · PLUG AND PLAY
                  </span>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 font-['Syne']" data-id="element-148">
                    Persona & Brand Intelligence
                  </h4>
                  <p className="text-gray-600 text-sm" data-id="element-149">
                    Voice, tone, communication style, brand consistency. An
                    agent needs these exactly like a conversation does. If
                    you've built guardrails with Synthia today, they port
                    directly to your agents.
                  </p>
                </div>
              </div>
            </div>

            {/* Layer 2 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:border-blue-200 transition-colors" data-id="element-150">
              <div className="flex items-start gap-4" data-id="element-151">
                <div className="p-2 bg-blue-100 rounded-full text-blue-600 mt-1" data-id="element-152">
                  <RefreshCw className="w-4 h-4" data-id="element-153" />
                </div>
                <div data-id="element-154">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2 block" data-id="element-155">
                    ALREADY BUILT · REFRAMED FOR AGENTS
                  </span>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 font-['Syne']" data-id="element-156">
                    Topic Boundaries & Escalation
                  </h4>
                  <p className="text-gray-600 text-sm" data-id="element-157">
                    The substance is the same but the frame shifts — from
                    "respond this way" to "behave this way." Synthia handles the
                    translation automatically, so your existing compliance work
                    carries forward.
                  </p>
                </div>
              </div>
            </div>

            {/* Layer 3 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 hover:border-teal-200 transition-colors" data-id="element-158">
              <div className="flex items-start gap-4" data-id="element-159">
                <div className="p-2 bg-teal-100 rounded-full text-teal-600 mt-1" data-id="element-160">
                  <Diamond className="w-4 h-4" data-id="element-161" />
                </div>
                <div data-id="element-162">
                  <span className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-2 block" data-id="element-163">
                    NEW · CONSTITUTIONAL AUTHORING
                  </span>
                  <h4 className="text-lg font-bold text-gray-900 mb-2 font-['Syne']" data-id="element-164">
                    Decision Authority & Behavioral Boundaries
                  </h4>
                  <p className="text-gray-600 text-sm" data-id="element-165">
                    What can this agent decide on its own? When must it
                    escalate? How does it behave when it doesn't know? This is
                    the governance layer unique to autonomous agents — and the
                    genuinely new work Synthia guides you through.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}